import type * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenWelcome } from '@gitbook/app-navigation';
import { webConfig } from '@gitbook/env';
import { toast } from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';
import { normalizeURLSlug } from '@gitbook/util-url';

import { createAndWaitForOpenAPISpec } from '../../Components';
import {
    apiClient,
    clearOnboardingParam,
    getOnboardingParam,
    OnboardingParamName,
} from '../../state';
import { CreateOrganizationScreen } from './CreateOrganizationScreen';
import { OnboardingScreen } from './OnboardingScreen';

/**
 * First screen as part of the user onboarding.
 * Also used when creating an organization.
 */
export default function WelcomeScreen(props: {
    screen: NavigationScreenWelcome;

    // flow controls
    onNext: ({ organization, site }: { organization: api.Organization; site: api.Site }) => void;
}): React.ReactElement {
    const { screen, onNext } = props;

    // After the org is created, or selected from a list, we create a Ultimate site and,
    // optionally, import the OpenAPI spec we received via the onboarding parameter.
    async function handleOnNext({ organization }: { organization: api.Organization }) {
        const site = await createSite({ organization });

        if (!site) {
            return;
        }

        // TODO: this could be moved all to the backend, as part of createSite
        const openapiOnboardingParam = getOnboardingParam(OnboardingParamName.OPENAPI);
        if (openapiOnboardingParam) {
            await importOpenApiSpecInSite({
                organization,
                site,
                openApiSpecURL: openapiOnboardingParam,
            });
        }

        onNext({ organization, site });
    }

    // The reason for upgrading is passed as a hash parameter
    const reason = screen.hash;

    useHeadTitle('Welcome');

    return (
        <div className="flex flex-1 bg-muted">
            {screen.organizationType ? (
                <CreateOrganizationScreen reason={reason} onNext={handleOnNext} />
            ) : (
                <OnboardingScreen onNext={handleOnNext} />
            )}
        </div>
    );
}

async function createSite({
    organization,
}: {
    organization: api.Organization;
}): Promise<api.Site | undefined> {
    const type = api.SiteType.Ultimate;

    return await toast.catch(async () => {
        const { data: site } = await apiClient.sites.createSite(organization.id, {
            type,
            title: `${organization.title} Docs`,
        });

        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organization.id },
                {
                    type: 'site_create_from_wizard',
                    inputName: site.title,
                    inputContent: 'empty',
                    inputFeatures: type,
                }
            )
        );

        return site;
    }, 'Error while creating site');
}

async function importOpenApiSpecInSite({
    organization,
    site,
    openApiSpecURL,
}: {
    organization: api.Organization;
    site: api.Site;
    openApiSpecURL: string;
}) {
    await toast.catch(async () => {
        const apiReferenceTemplate = webConfig.contentTemplates.find(
            (template) => template.id === 'builtin:api-reference'
        );

        if (!apiReferenceTemplate) {
            throw new Error('API Reference template not found');
        }

        const slug = normalizeURLSlug(site.title);

        // Create a computed openapi space and import the OpenAPI spec
        const [{ data: space }] = await Promise.all([
            apiClient.spaces.createSpace(organization.id, {
                computedSource: {
                    type: 'builtin:openapi',
                    props: {
                        models: true,
                    },
                    dependencies: {
                        spec: {
                            ref: {
                                kind: 'openapi',
                                spec: slug,
                            },
                        },
                    },
                },
                emoji: 'emoji' in apiReferenceTemplate ? apiReferenceTemplate.emoji : undefined,
                title: apiReferenceTemplate.title,
            }),
            apiReferenceTemplate.openAPISpec
                ? createAndWaitForOpenAPISpec({
                      openAPISpec: {
                          slug,
                          sourceURL: openApiSpecURL,
                      },
                      organizationId: organization.id,
                  })
                : null,
        ]);

        clearOnboardingParam(OnboardingParamName.OPENAPI);

        // Add the new space to the site
        await apiClient.siteSpaces.addSpaceToSite(organization.id, site.id, {
            spaceId: space.id,
        });
    }, 'Error while importing the OpenAPI spec');
}
